import { CheckCircle, Hourglass, ClockCountdown } from "@phosphor-icons/react";
import { MONTH_STATUS_CLS, fmtHours } from "../../lib/regularisation";

const ICON = { Finalised: CheckCircle, "Awaiting Approval": Hourglass, Provisional: ClockCountdown };

export function MonthStatusBadge({ status, testId = "month-status-badge" }) {
    const Icon = ICON[status] || ClockCountdown;
    return (
        <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${MONTH_STATUS_CLS[status] || "bg-slate-50 text-slate-700 border-slate-200"}`} data-testid={testId}>
            <Icon size={13} weight="bold" /> {status}
        </span>
    );
}

function Tile({ label, value, hint, tone = "text-slate-900", testId }) {
    return (
        <div className="rounded-lg border border-slate-200 bg-white px-3 py-2.5" data-testid={testId}>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
            <div className={`text-xl font-bold tabular-nums ${tone}`}>{value}</div>
            {hint && <div className="text-[11px] text-slate-500">{hint}</div>}
        </div>
    );
}

export function MonthDaysPanel({ month }) {
    const d = month.days || {};
    const h = month.hours || {};
    const diffTone = h.difference_hours == null ? "text-slate-700" : h.difference_hours < 0 ? "text-rose-700" : "text-emerald-700";
    return (
        <div className="space-y-4" data-testid="month-days-panel">
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
                <Tile label="Scheduled days" value={d.scheduled_working_days ?? 0} hint="working days so far" testId="tile-scheduled" />
                <Tile label="Full days" value={d.worked_full_days ?? 0} tone="text-emerald-700" testId="tile-full" />
                <Tile label="Half days" value={d.worked_half_days ?? 0} tone="text-amber-700" testId="tile-half" />
                <Tile label="Leave" value={d.leave_days ?? 0} tone="text-sky-700" testId="tile-leave" />
                <Tile label="Absence" value={d.absence_days ?? 0} tone="text-rose-700" testId="tile-absence" />
                <Tile label="Needs action" value={d.pending_days ?? 0} tone={d.pending_days ? "text-amber-700" : "text-slate-900"} testId="tile-pending" />
                <Tile label="Weekly offs" value={d.weekly_offs ?? 0} testId="tile-weekly-offs" />
                <Tile label="Holidays" value={d.holidays ?? 0} testId="tile-holidays" />
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-4 grid sm:grid-cols-3 gap-4" data-testid="hours-comparison">
                <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">Approved worked hours</div>
                    <div className="text-2xl font-bold tabular-nums text-slate-900" data-testid="hours-approved">{fmtHours(h.approved_worked_hours)}</div>
                    {h.credited_hours > 0 && <div className="text-[11px] text-slate-500" data-testid="hours-credited-note">{fmtHours(h.recorded_hours)} recorded + {fmtHours(h.credited_hours)} credited by approved regularisation (assigned shift hours, missing part only)</div>}
                </div>
                <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">Expected for worked days</div>
                    <div className="text-2xl font-bold tabular-nums text-slate-900" data-testid="hours-expected">{fmtHours(h.expected_hours)}</div>
                    <div className="text-[11px] text-slate-500">{(h.basis_lines || []).join(" + ") || "No shift hours on worked days"}</div>
                </div>
                <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">Difference</div>
                    <div className={`text-2xl font-bold tabular-nums ${diffTone}`} data-testid="hours-difference">{h.comparison || "—"}</div>
                    <div className="text-[11px] text-slate-500">{h.note}</div>
                </div>
            </div>
        </div>
    );
}

export default MonthDaysPanel;
